import isNumber from 'lodash/isNumber';
import {ISSUE_ATTRIBUTE_NAMES} from '../constants/issue-attribute-names';
import i18n from './i18n';
import {ISSUE_ATTRIBUTES_DATA_TYPES} from '../constants/issue-attributes';
import {TEMPLATE_ISSUE_ATTRIBUTES_FIELD_NAMES} from '../constants/issue-templates';

const DESCRIPTION_MAX_LENGTH = 2500;
const TITLE_QUALITY_ISSUE_MAX_LENGTH = 150;
const LOCATION_DETAILS_MAX_LENGTH = 250;
export const TEMPLATE_CATEGORY_TITLE_MAX_LENGTH = 100;
const TEMPLATE_DUE_DATE_MAX_VALUE = 9999;

export const presenceValidator = val =>
  typeof val !== 'undefined' &&
  val !== null &&
  (!val.trim || val.trim()) &&
  (val.length || !val.hasOwnProperty('length'));

export const maxLengthValidator = maxLength => text => !text || text.length <= maxLength;

const maxCharactersValidation = (value, limit) =>
  !maxLengthValidator(limit)(value) && i18n.t('new.maxCharactersValidationMessage', {limit});

export const uniqueValueValidator = texts => text => !text || !texts.includes(text.trim().toLowerCase());

export const validateQualityIssueDescription = descriptionValue =>
  maxCharactersValidation(descriptionValue, DESCRIPTION_MAX_LENGTH);

export const validateLocationDetails = locationDetails =>
  maxCharactersValidation(locationDetails, LOCATION_DETAILS_MAX_LENGTH);

export const validateQualityIssueTitle = titleValue =>
  maxCharactersValidation(titleValue, TITLE_QUALITY_ISSUE_MAX_LENGTH);

export const getInputValidationMessage = (attributeName, inputValue) => {
  switch (attributeName) {
    case ISSUE_ATTRIBUTE_NAMES.DESCRIPTION:
      return validateQualityIssueDescription(inputValue);
    case ISSUE_ATTRIBUTE_NAMES.TITLE:
      return validateQualityIssueTitle(inputValue);
    case ISSUE_ATTRIBUTE_NAMES.LOCATION_DETAILS:
      return validateLocationDetails(inputValue);
    default:
      return null;
  }
};

/**
 * Validates a string (supposedly numeric) has no space(s)
 */
const validateNumericHasNoSpace = text =>
  !(!text || !text.includes(' ')) && i18n.t('validationMessages.numericSpaceError');

/**
 * Validates that an input is a number
 */
const validateNumericalValue = text =>
  !(!text || (text.length === 1 && text[0] === '-') || !isNaN(text)) && i18n.t('validationMessages.nanError');

/**
 * Validates a numeric string has no commas after a number
 */
const validateNumericHasNoComma = text =>
  !(!text || !/[0-9],/g.test(text)) && i18n.t('validationMessages.numericCommaError');

/**
 * Validates a numeric string has no double periods
 */
const validateNumericHasNoDoublePeriods = text =>
  !(!text || !(text.replace(/[^.]/g, '').length > 1)) && i18n.t('validationMessages.numericDoublePeriodError');

/**
 * Validates a numeric string doesn't include numbers and letters
 */
const validateNumericEdgeCase = text =>
  !(!text || !(isNaN(text) && /\d/.test(text))) && i18n.t('validationMessages.numericValidNumber');

/**
 * Validates a numeric string doesn't include more than {maxLength} digits after decimal point
 * @param text
 * @param maxLength
 */
const validateNumericMaxFloat = (text, maxLength) => {
  const expression = new RegExp(`^-?\\d*\\.?\\d{0,${maxLength}}$`);
  return !(!text || expression.test(text)) && i18n.t('validationMessages.numericMaxFloat', {maxLength});
};

/**
 * Validates a numeric string doesn't include more than {maxLength} characters
 * @param text
 * @param maxLength
 */
const validateNumericMaxLength = (text, maxLength) =>
  !(!text || text.length <= maxLength) && i18n.t('validationMessages.numericMaxLength', {maxLength});

const validateIsNumber = number => !(!number || isNumber(number)) && i18n.t('validationMessages.numericValidNumber');

const validateNumericMaxValue = (number, maxValue) =>
  !(!number || number <= maxValue) && i18n.t('validationMessages.numericMaxValue', {maxValue});

export const commonNumericalValidations = text =>
  validateNumericHasNoSpace(text) ||
  validateNumericHasNoComma(text) ||
  validateNumericHasNoDoublePeriods(text) ||
  validateNumericEdgeCase(text) ||
  validateNumericalValue(text) ||
  validateNumericMaxFloat(text, 5) ||
  validateNumericMaxLength(text, 15);

export const numericValueValidations = (number, maxValue) =>
  validateIsNumber(number) || validateNumericMaxValue(number, maxValue);

export const getIssueCustomAttributeInputValidationMessage = (dataType, inputValue) => {
  switch (dataType) {
    default:
    case ISSUE_ATTRIBUTES_DATA_TYPES.SINGLE_LIST:
      return null;
    case ISSUE_ATTRIBUTES_DATA_TYPES.PARAGRAPH: {
      const result = maxCharactersValidation(inputValue, 2500);
      return result ? result : '';
    }
    case ISSUE_ATTRIBUTES_DATA_TYPES.TEXT: {
      const result = maxCharactersValidation(inputValue, 250);
      return result ? result : '';
    }
    case ISSUE_ATTRIBUTES_DATA_TYPES.NUMERIC: {
      const result = commonNumericalValidations(inputValue);
      return result ? result : '';
    }
  }
};

export const getTemplateInputValidationMessage = (attributeName, value) => {
  switch (attributeName) {
    case TEMPLATE_ISSUE_ATTRIBUTES_FIELD_NAMES.DESCRIPTION:
      return maxCharactersValidation(value, DESCRIPTION_MAX_LENGTH);
    case TEMPLATE_ISSUE_ATTRIBUTES_FIELD_NAMES.TITLE:
      return maxCharactersValidation(value, TITLE_QUALITY_ISSUE_MAX_LENGTH);
    case TEMPLATE_ISSUE_ATTRIBUTES_FIELD_NAMES.LOCATION_DETAILS:
      return maxCharactersValidation(value, LOCATION_DETAILS_MAX_LENGTH);
    case TEMPLATE_ISSUE_ATTRIBUTES_FIELD_NAMES.DUE_DATE:
      return numericValueValidations(value, TEMPLATE_DUE_DATE_MAX_VALUE);
    default:
      return null;
  }
};

export const getCategoryTitleValidationMessage = (title, templateCategoryTitles) => {
  const isCategoryUnique = uniqueValueValidator(templateCategoryTitles);
  return (
    (!presenceValidator(title) && i18n.t('new.fieldRequired')) ||
    maxCharactersValidation(title, TEMPLATE_CATEGORY_TITLE_MAX_LENGTH) ||
    (!isCategoryUnique(title) && i18n.t('createCategory.title.categoryExists')) ||
    ''
  );
};
